import type { InterviewDetail, InterviewSummary } from "@/api/generated/types";

type InterviewStatus = InterviewSummary["status"];
type Recommendation = InterviewSummary["recommendation"];

export type InterviewDisplayStatus =
  | "新規応募"
  | "未対応"
  | "面談中"
  | "評価中"
  | "面談完了"
  | "評価失敗"
  | "中断";

const NEW_APPLICATION_HOURS = 24;

const statusLabels: Record<InterviewStatus, string> = {
  SCHEDULED: "予定",
  READY: "準備完了",
  LIVE: "面談中",
  EVALUATING: "評価中",
  COMPLETED: "完了",
  FAILED: "評価失敗",
  CANCELLED: "中断",
};

const recommendationLabels: Record<string, string> = {
  S: "S — 卓越",
  A: "A — 優秀",
  B: "B — 良好",
  C: "C — 標準",
  D: "D — 不十分",
};

export function formatInterviewStatus(status: InterviewStatus): string {
  // 既知の status は日本語ラベルへ。将来サーバ側で追加された未知の状態値でも undefined を描画しないよう原値にフォールバックする。
  return statusLabels[status] ?? status;
}

export function formatRecommendation(recommendation: Recommendation): string {
  if (!recommendation) {
    return "未評価";
  }

  return recommendationLabels[recommendation] ?? recommendation;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return "未設定";
  }

  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export function formatRelativeScore(score: number | null | undefined): string {
  if (typeof score !== "number") {
    return "未評価";
  }

  return `${score} / 100`;
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) {
    return "未登録";
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${Math.ceil(bytes / 1024)} KB`;
}

export function getInterviewDisplayTitle(detail: InterviewDetail): string {
  return detail.interview.templateSnapshotJson.title;
}

export function mapInterviewStatusToDisplay(
  status: InterviewStatus,
  createdAt: string,
  now: Date = new Date()
): InterviewDisplayStatus {
  if (status === "COMPLETED") {
    return "面談完了";
  }

  if (status === "LIVE") {
    return "面談中";
  }

  if (status === "EVALUATING") {
    return "評価中";
  }

  if (status === "FAILED") {
    return "評価失敗";
  }

  if (status === "CANCELLED") {
    return "中断";
  }

  const hoursSinceCreated = (now.getTime() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
  return hoursSinceCreated < NEW_APPLICATION_HOURS ? "新規応募" : "未対応";
}

export function formatRelativeTime(
  value: string | null | undefined,
  now: Date = new Date()
): string {
  if (!value) {
    return "";
  }

  const target = new Date(value).getTime();
  const diffMs = now.getTime() - target;

  if (diffMs < 0) {
    return "間もなく";
  }

  const minutes = Math.floor(diffMs / (60 * 1000));
  if (minutes < 1) {
    return "たった今";
  }
  if (minutes < 60) {
    return `${minutes}分前`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}時間前`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days}日前`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months}ヶ月前`;
  }

  const years = Math.floor(months / 12);
  return `${years}年前`;
}

export function formatDateShort(value: string | null | undefined): string {
  if (!value) {
    return "未設定";
  }

  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));
}

export function getDisplayName(name: string | null | undefined): string {
  if (!name || name.trim().length === 0) {
    return "未設定";
  }
  return name;
}

export function getInitials(name: string): string {
  const cleaned = name.trim();
  if (cleaned.length === 0) {
    return "?";
  }

  const tokens = cleaned.split(/\s+/).filter((t) => t.length > 0);
  if (tokens.length >= 2) {
    return `${tokens[0]?.charAt(0) ?? ""}${tokens[1]?.charAt(0) ?? ""}`.toUpperCase() || "?";
  }

  return cleaned.slice(0, 2).toUpperCase();
}
